import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="text-xl font-bold text-foreground">
              VibeSprint
            </a>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">
          Last updated: January 2026
        </p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              VibeSprint ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our AI Product Decision Engine service.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              By using VibeSprint, you agree to the collection and use of information 
              in accordance with this policy. If you do not agree with our policies and 
              practices, please do not use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-6">2.1 Information You Provide</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect information that you voluntarily provide to us, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Feedback text and user signals you import or paste into the platform</li>
              <li>Account information (when authentication is enabled)</li>
              <li>Communication with our support team (support@vibesprint.ai)</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you use VibeSprint, we automatically collect certain information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Usage data (pages visited, features used, time spent)</li>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and approximate geographic location</li>
              <li>Analytics data to improve our service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>To provide and maintain our AI decision engine service</li>
              <li>To analyze feedback and generate product decisions using AI</li>
              <li>To improve and personalize your experience</li>
              <li>To send service-related notifications and updates</li>
              <li>To respond to your inquiries and support requests</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Data Processing and AI</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              VibeSprint uses an automated analysis engine
              to analyze feedback and generate product decisions. When you submit feedback:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Lane analysis runs in our automated analysis engine</li>
              <li>Optional changelog drafts and weekly digest emails use third-party AI and email service providers to process that data on our behalf — see section 8</li>
              <li>Submitted signals, decisions, and related records are stored with a managed database provider</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              The current service handles requests as a shared demo user.
              Per-user authentication is not enabled, so submitted content is not
              scoped to an individual login.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Data Storage and Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Submitted signals, decisions, roadmap items, changelog entries, and related
              workspace records are stored with a managed database provider. The current service handles
              requests as a shared demo user; per-user authentication is not enabled.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You can download a JSON archive of the active workspace from Settings → Data export
              (signals, decisions, roadmap items, and changelog entries).
            </p>
            <p className="text-muted-foreground leading-relaxed">
              No method of transmission over the internet or electronic storage
              is 100% secure. We do not promise account-level isolation while
              authentication is disabled.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We retain stored records while they remain in the service database and
              are used to provide the product.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              To request deletion, email support@vibesprint.ai.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Your Rights (GDPR & CCPA)</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing of your data</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, contact us at support@vibesprint.ai. We will respond 
              within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use third-party AI and email service providers to process data on our behalf.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              VibeSprint uses the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Managed database provider</strong> - for data storage</li>
              <li><strong>Analytics providers</strong> - for usage tracking</li>
              <li><strong>Third-party AI and email providers</strong> - to process feedback and send notifications</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              These services have their own privacy policies. We encourage you to review them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use minimal cookies and tracking technologies for essential functionality 
              and analytics. We do not use cookies for advertising or cross-site tracking. 
              You can control cookies through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              VibeSprint is not intended for users under 18 years of age. We do not 
              knowingly collect personal information from children. If you believe we 
              have collected data from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and processed in countries other than 
              your country of residence. We ensure appropriate safeguards are in place for 
              such transfers in compliance with applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of 
              any material changes by posting the new policy on this page and updating the 
              "Last updated" date. Your continued use of VibeSprint after changes constitutes 
              acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, 
              please contact us:
            </p>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-foreground"><strong>Email:</strong> support@vibesprint.ai</p>
              <p className="text-foreground mt-2"><strong>Service:</strong> VibeSprint - AI Product Decision Engine</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 VibeSprint. Built for founders who ship.
            </p>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
                Privacy
              </a>
              <a href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
                Terms
              </a>
              <a href="mailto:support@vibesprint.ai" className="text-sm text-muted-foreground hover:text-foreground">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
